import { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Loader2, Sparkles, Wand2 } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { invokeLlamaChat } from '@core/ai/llamaChat';
import { stripHtml } from '@core/utils/htmlPlain';

export type AiAssistPreset =
  | 'email_custom_html'
  | 'job_description'
  | 'application_internal_notes'
  | 'requisition_business_justification'
  | 'requisition_internal_notes'
  | 'requisition_role_title'
  | 'candidate_notes'
  | 'client_notes'
  | 'email_sequence_body'
  | 'scorecard_notes'
  | 'interview_event_notes'
  | 'generic_plain'
  | 'sourcing_talent_pool'
  | 'screening_fitment_summary'
  | 'offer_comp_draft'
  | 'email_thread_insight'
  | 'career_seo_public_listing'
  | 'reports_predictive_narrative'
  | 'email_template_from_brief'
  | 'compliance_retention_review'
  | 'interview_scheduling_assistant';

const SYSTEM: Record<AiAssistPreset, string> = {
  email_custom_html:
    'You improve recruiter email copy. Output plain text only (no markdown fences). Use short paragraphs separated by a blank line. Professional, warm, concise. Do not invent salary or offers.',
  job_description:
    'You write clear job descriptions for recruiters. Output plain text: sections Overview, Responsibilities, Requirements, Nice-to-have when relevant. No markdown code fences.',
  application_internal_notes:
    'You are a senior recruitment consultant writing internal ATS notes. Be concise and factual. Do not invent compensation. Plain text, bullets with leading - allowed.',
  requisition_business_justification:
    'You refine requisition / role summaries for hiring managers and approvers. Plain text, structured, business-appropriate.',
  requisition_internal_notes:
    'You write internal requisition notes for recruiters only. Plain text, concise, factual.',
  candidate_notes:
    'You refine internal candidate notes for an ATS. Plain text, professional, no invented facts.',
  client_notes:
    'You refine internal notes about a client organisation. Plain text, professional.',
  email_sequence_body:
    'You improve drip-email body copy. Preserve merge tokens like {{candidate_name}} exactly when present. Output plain HTML allowed only if input was HTML — otherwise plain text with line breaks.',
  scorecard_notes:
    'You help interviewers phrase fair scorecard / feedback notes. Plain text, constructive, factual.',
  interview_event_notes:
    'You refine interview scheduling notes (logistics, links, context). Plain text, concise.',
  generic_plain:
    'You improve the user\'s text for a professional recruiting context. Output plain text only.',
  requisition_role_title:
    'You write concise ATS job titles for staffing requisitions. Output a single line title only (no quotes, no markdown).',
  sourcing_talent_pool:
    'You help recruiters with talent-pool strategy. Output plain text: bullets for Boolean search strings, outreach angles, and red flags to verify. Do not invent candidate facts.',
  screening_fitment_summary:
    'You summarize screening decisions for recruiters. Plain text: strengths, gaps, risk flags, recommended next step. Be evidence-based; do not invent facts beyond the context.',
  offer_comp_draft:
    'You draft internal offer talking points for recruiters (not legal contracts). Plain text bullets: role summary, comp structure placeholders, start date, contingencies. No fabricated numbers unless in context.',
  email_thread_insight:
    'You analyze recruiting email threads. Plain text: (1) intent classification, (2) suggested reply tone, (3) 2–3 follow-up timing hints (business hours, urgency). No legal advice.',
  career_seo_public_listing:
    'You write short public-facing job blurbs for career pages and SEO. Plain text: line 1 = meta title (≤60 chars ideal), line 2 blank, then 2–3 sentence summary with location and role type. No internal codes.',
  reports_predictive_narrative:
    'You interpret recruitment funnel metrics for operators. Plain text: anomalies, bottlenecks, 3 actionable hypotheses, 3 next steps. State uncertainty; not a statistical model.',
  email_template_from_brief:
    'You draft org email templates. Output ONLY a single JSON object, no markdown fences, with keys "subject" (string) and "body_html" (string, simple HTML paragraphs). Use {{merge}} tokens like {{candidate_name}} where appropriate.',
  compliance_retention_review:
    'You summarize data-retention posture for HR/recruiting admins. Plain text: checklist of risks, DPA/consent reminders, operational next steps. Not legal advice; recommend counsel for jurisdiction-specific rules.',
  interview_scheduling_assistant:
    'You help schedule interviews. Plain text: candidate-facing slot proposal copy, internal checklist (timezone, video link, panel), and polite reschedule wording. Concise.',
};

const DEFAULT_TASK: Record<AiAssistPreset, string> = {
  email_custom_html: 'Improve clarity and tone. Keep the same intent. If empty, draft a short professional paragraph introducing the update.',
  job_description: 'Expand or tighten this JD based on the context. Keep truthful — do not invent tools or years.',
  application_internal_notes:
    'Write internal submission notes: fit vs role, strengths, gaps, suggested next step. Bullet list.',
  requisition_business_justification: 'Sharpen this justification for approvers: problem, scope, success criteria.',
  requisition_internal_notes: 'Summarize risks, stakeholders, and timeline hints from the context.',
  candidate_notes: 'Polish these notes for handoff to another recruiter.',
  client_notes: 'Polish internal client notes; flag open questions as bullets.',
  email_sequence_body: 'Improve readability while preserving all {{merge}} tokens exactly.',
  scorecard_notes: 'Tighten wording; keep balanced and evidence-based.',
  interview_event_notes: 'Make logistics and expectations crystal clear.',
  generic_plain: 'Improve clarity and tone while preserving meaning.',
};

const CHIPS: Partial<Record<AiAssistPreset, { label: string; task: string }[]>> = {
  application_internal_notes: [
    {
      label: 'Submission rationale',
      task: 'Bullet internal notes: fit vs job, top strengths, gaps or risks, suggested next step for the client.',
    },
    {
      label: 'Due-diligence',
      task: 'List 5 questions the recruiter should confirm before client submittal (skills, comp, notice, competition).',
    },
    {
      label: 'Client intro',
      task: 'Draft a short client-facing email intro (3–4 sentences) after internal review — no salary unless in text.',
    },
  ],
  job_description: [
    { label: 'Shorten', task: 'Condense to essentials; keep structure.' },
    { label: 'Inclusive', task: 'Rewrite requirements to be inclusive; avoid biased language.' },
  ],
  email_custom_html: [
    { label: 'Warmer', task: 'Slightly warmer tone; still professional.' },
    { label: 'Shorter', task: 'Cut length by ~30%; keep key facts.' },
  ],
  email_thread_insight: [
    { label: 'Urgent?', task: 'Is the candidate or client urgent? Classify and suggest response SLA in hours.' },
    { label: 'Send windows', task: 'Suggest best send times (timezone-aware) for a follow-up email.' },
  ],
  sourcing_talent_pool: [
    { label: 'Boolean search', task: 'Propose 3 LinkedIn-style Boolean strings to find similar profiles.' },
    { label: 'Outreach hook', task: '3 short personalized outreach angles (no fabricated employer facts).' },
  ],
  reports_predictive_narrative: [
    { label: 'Bottleneck', task: 'Which funnel stage looks stuck vs typical agency flow? Why might that be?' },
    { label: 'Client risk', task: 'Which client metrics hint at relationship or delivery risk?' },
  ],
};

export interface ContextAiAssistModalProps {
  open: boolean;
  onClose: () => void;
  /** Falls back to useAuth().user.orgId */
  orgId?: string | null;
  /** Falls back to useAuth().user.orgUserId */
  orgUserId?: string | null;
  /** Logged to ggj_b2b_ai_usage_log */
  feature: string;
  preset: AiAssistPreset;
  title?: string;
  contextHint?: string;
  original: string;
  originalFormat?: 'plain' | 'html';
  onApply: (text: string, mode: 'replace' | 'append') => void;
  /** If false, hide append (e.g. JD replace only). */
  allowAppend?: boolean;
  /** If false, applying AI text runs onApply but keeps the dialog open (e.g. copy-only flows). */
  closeOnApply?: boolean;
  /** If false, hide replace/append (read-only suggestion — user copies from the panel). */
  showApplyActions?: boolean;
}

export function ContextAiAssistModal({
  open,
  onClose,
  orgId: orgIdProp,
  orgUserId: orgUserIdProp,
  feature,
  preset,
  title,
  contextHint,
  original,
  originalFormat = 'plain',
  onApply,
  allowAppend = true,
  closeOnApply = true,
  showApplyActions = true,
}: ContextAiAssistModalProps) {
  const { user } = useAuth();
  const orgId = orgIdProp ?? user?.orgId ?? null;
  const orgUserId = orgUserIdProp ?? user?.orgUserId ?? null;

  const originalDisplay = useMemo(() => {
    if (originalFormat === 'html') return stripHtml(original) || '(empty)';
    return original.trim() || '(empty)';
  }, [original, originalFormat]);

  const userMessageBase = useMemo(() => {
    const body =
      originalFormat === 'html'
        ? `Current message (HTML stripped for analysis):\n${stripHtml(original) || '—'}`
        : `Current text:\n${original.trim() || '—'}`;
    const hint = contextHint?.trim() ? `\n\nContext:\n${contextHint.trim()}` : '';
    return `${body}${hint}`;
  }, [original, originalFormat, contextHint]);

  const [task, setTask] = useState(DEFAULT_TASK[preset]);
  const [aiText, setAiText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTask(DEFAULT_TASK[preset]);
      setAiText('');
      setError(null);
    }
  }, [open, preset]);

  const run = useCallback(async () => {
    if (!orgId) {
      setError('Organisation required for AI.');
      return;
    }
    setError(null);
    setLoading(true);
    setAiText('');
    try {
      const { content } = await invokeLlamaChat(
        [
          { role: 'system', content: SYSTEM[preset] },
          {
            role: 'user',
            content: `${userMessageBase}\n\n---\n\nTask:\n${task.trim() || DEFAULT_TASK[preset]}`,
          },
        ],
        undefined,
        { orgId, feature, orgUserId }
      );
      setAiText(content.trim());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'AI request failed');
    } finally {
      setLoading(false);
    }
  }, [orgId, orgUserId, feature, preset, task, userMessageBase]);

  if (!open) return null;

  const chips = CHIPS[preset] ?? [];

  return (
    <div
      className="fixed inset-0 z-[500] flex items-center justify-center p-4 bg-black/65"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ai-assist-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl max-h-[min(88vh,720px)] flex flex-col rounded-xl border vscode-border vscode-bg-elevated shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between gap-3 px-3 py-2 rounded-t-xl shrink-0"
          style={{ backgroundColor: 'var(--brand-popup-header)' }}
        >
          <h2 id="ai-assist-title" className="text-sm font-semibold text-white flex items-center gap-2">
            <span className="flex items-center justify-center w-7 h-7 rounded-md bg-white/15 shrink-0">
              <Sparkles className="w-4 h-4 text-white" />
            </span>
            {title ?? '[AI Assist]'}
          </h2>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/15" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-3 py-2 border-b vscode-border shrink-0 space-y-1.5">
          {!orgId && <p className="text-sm text-amber-400">Join an organisation to use AI (Groq / ggj_b2b_llama).</p>}
          {chips.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {chips.map((c) => (
                <button
                  key={c.label}
                  type="button"
                  className="text-xs px-2 py-1 rounded-lg border vscode-border vscode-text-secondary hover:vscode-bg-hover"
                  onClick={() => setTask(c.task)}
                >
                  {c.label}
                </button>
              ))}
            </div>
          )}
          <label className="block text-xs vscode-text-tertiary">Instructions for the model</label>
          <textarea
            className="input w-full min-h-[52px] text-sm"
            value={task}
            onChange={(e) => setTask(e.target.value)}
            disabled={loading}
          />
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-2 text-sm"
            disabled={loading || !orgId}
            onClick={() => void run()}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            {aiText ? 'Regenerate' : 'Generate'}
          </button>
        </div>

        <div className="flex-1 min-h-0 grid md:grid-cols-2 gap-0 md:divide-x vscode-border overflow-hidden">
          <div className="flex flex-col min-h-0 p-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary mb-1">Original</p>
            <pre className="flex-1 min-h-[96px] max-h-[min(28vh,240px)] overflow-y-auto whitespace-pre-wrap text-sm vscode-text-primary font-sans rounded-lg border vscode-border p-2 vscode-bg-tertiary/40">
              {originalDisplay}
            </pre>
            {originalFormat === 'html' && (
              <p className="text-[10px] vscode-text-tertiary mt-1">HTML fields show a text preview on the left; generation uses stripped content.</p>
            )}
          </div>
          <div className="flex flex-col min-h-0 p-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary mb-1">AI suggestion</p>
            {error && <p className="text-sm text-red-400 mb-2">{error}</p>}
            {loading && (
              <div className="flex items-center gap-2 text-sm vscode-text-tertiary py-4 justify-center">
                <Loader2 className="w-5 h-5 animate-spin" /> Generating…
              </div>
            )}
            {!loading && (
              <pre className="flex-1 min-h-[96px] max-h-[min(28vh,240px)] overflow-y-auto whitespace-pre-wrap text-sm vscode-text-primary font-sans rounded-lg border vscode-border p-2 vscode-bg-tertiary/40">
                {aiText || '— Run Generate to see a suggestion —'}
              </pre>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-t vscode-border shrink-0">
          {showApplyActions && (
            <>
              <button
                type="button"
                className="btn-primary text-sm"
                disabled={!aiText.trim()}
                onClick={() => {
                  onApply(aiText.trim(), 'replace');
                  if (closeOnApply) onClose();
                }}
              >
                Use AI text (replace)
              </button>
              {allowAppend && (
                <button
                  type="button"
                  className="btn-secondary text-sm"
                  disabled={!aiText.trim()}
                  onClick={() => {
                    onApply(aiText.trim(), 'append');
                    if (closeOnApply) onClose();
                  }}
                >
                  Append AI text
                </button>
              )}
            </>
          )}
          <button type="button" className="btn-secondary text-sm ml-auto" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export function AiAssistIconButton({
  onClick,
  disabled,
  title: ariaTitle,
}: {
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={ariaTitle ?? 'AI assist — compare original with generated text'}
      className="inline-flex items-center justify-center rounded-lg p-1.5 border border-brand-600/40 bg-brand-600/10 text-brand-400 hover:bg-brand-600/20 hover:text-brand-300 disabled:opacity-40 disabled:pointer-events-none transition-colors"
      aria-label={ariaTitle ?? 'Open AI assist'}
    >
      <Sparkles className="w-4 h-4" />
    </button>
  );
}
